// Body of the anonymous order-STATUS page (/orders/<number>/<hash>/): the
// legacy sealy layout -- the "Your Orders" heading, the shared RichOrderDetail
// block (the collapsible order-summary-details panel + grouped lines with
// named-status progress bars, typed-serialized server-side), the two legacy
// empty states, and the customer-service info block. The order-details toggle
// is owned by the shared block's own disclosure state (collapsible).
import { Immutable } from "immer";
import React from "react";
import { RichOrderDetail } from "tsi-common-react/src/apps/customer-account/RichOrderDetail/index";

import { templates } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { CustomerServiceInfoBlock } from "../CustomerServiceInfoBlock";

import orderListStyles from "../../styles/composable/order-list.module.scss";
import styles from "./index.module.scss";

type IProps = Immutable<templates.AnonOrderDocumentTemplate>;

export const AnonOrderPageBody = (props: IProps): React.JSX.Element => {
    const { order } = props;
    return (
        // The `order-detail` id stays: the legacy `#order-detail .progress-bar`
        // rule styles the shared RichOrderDetail block.
        <section
            id="order-detail"
            className={concatClassNames([
                "l-capped-width",
                "u-margin-bottom--xl",
                "u-full-width-title",
                orderListStyles.orderList,
                styles.orderDetail,
            ])}
            data-testid="order-list"
        >
            <h2>Your Orders</h2>

            {/* The empty-order-status modifier is ALWAYS on: the legacy
                template gated it on `{% if not orders %}`, but the view
                never defines `orders`, so the class rendered on every
                request -- including with-order renders -- and the mobile
                orders-page margins key on it. */}
            <div
                className={concatClassNames([
                    "content__copy--full-width",
                    orderListStyles.ordersContent,
                    orderListStyles.ordersContentEmpty,
                ])}
                data-testid="order-list-content"
            >
                <div className={orderListStyles.orders}>
                    <ul className={orderListStyles.ordersList}>
                        {order.has_order ? (
                            <li
                                className={orderListStyles.ordersListItem}
                                data-testid="order-list-item"
                            >
                                <div
                                    className={concatClassNames([
                                        "content__copy--full-width",
                                        orderListStyles.orderSummaryWrapper,
                                    ])}
                                >
                                    <RichOrderDetail
                                        summary={order.summary}
                                        shipped_events={order.shipped_events}
                                        unshipped={order.unshipped}
                                        collapsible={true}
                                    />
                                </div>
                            </li>
                        ) : order.form_is_bound ? (
                            <li
                                className={orderListStyles.ordersListItem}
                                data-testid="order-list-item"
                            >
                                No orders match your search.
                            </li>
                        ) : (
                            <li
                                className={concatClassNames([
                                    orderListStyles.ordersListItem,
                                    orderListStyles.emptyListItem,
                                ])}
                                data-testid="order-list-item"
                            >
                                <h2
                                    className={
                                        orderListStyles.emptyListItemTitle
                                    }
                                >
                                    You haven&apos;t shopped with us.
                                </h2>
                                <p
                                    className={
                                        orderListStyles.emptyListItemCopy
                                    }
                                >
                                    Please check back after your first purchase
                                    to view your order details.
                                </p>
                                <a className="button" href="/">
                                    Shop now
                                </a>
                            </li>
                        )}
                    </ul>
                </div>
            </div>

            {props.cs_info && <CustomerServiceInfoBlock info={props.cs_info} />}
        </section>
    );
};
